import {
    useEffect,
    Fragment,
    useState,
    useRef
} from "react";
import {
    StyleSheet,
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type IDialogProps,
    NCoreUIKitDialog,
    NCoreUIKitTheme,
    type IDialogRef,
    TextInput,
    Button,
    Dialog,
    Card,
    Text,
    Kbd
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Trash2
} from "lucide-react-native";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    createKitIcon
} from "../../components/kitIcons";
import {
    InfoIcon
} from "../../anatomy/icons";

const Trash2Icon = createKitIcon(Trash2);

const NAME = "Dialog";

const USAGE_CODE = `const [isOpen, setIsOpen] = useState(false);

<Dialog
    primaryButtonProps={{
        onPress: ({ closeAnimation }) => closeAnimation(),
        title: "Delete"
    }}
    secondaryButtonProps={{
        onPress: ({ closeAnimation }) => closeAnimation()
    }}
    onOverlayPress={({ closeAnimation }) => closeAnimation()}
    content="The draft will be deleted permanently."
    onClosed={() => setIsOpen(false)}
    title="Delete this draft?"
    isOpen={isOpen}
    variant="yes-no"
    type="danger"
/>`;

const IMPERATIVE_CODE = `NCoreUIKitDialog.open({
    primaryButtonProps: {
        onPress: ({ closeAnimation }) => closeAnimation(),
        title: "Got it"
    },
    onOverlayPress: ({ closeAnimation }) => closeAnimation(),
    content: "A new version is ready to install.",
    title: "Update available",
    type: "info"
});`;

const VARIANTS = [
    "yes-no",
    "ok",
    "info"
] as const;

const TYPES = [
    "neutral",
    "primary",
    "success",
    "warning",
    "danger",
    "info"
] as const;

const JUSTIFIES = [
    "centered",
    "language-based"
] as const;

const SIZES = [
    "xSmall",
    "small",
    "medium",
    "large"
] as const;

const SAVE_DELAY = 1200;

type DialogActionProps = {
    closeAnimation: () => void;
};

type DialogShowcaseProps = Pick<IDialogProps, "contentJustify" | "isShowClose" | "variant" | "size" | "type"> & {
    title: string;
};

const SHORTCUT_ROWS = [
    {
        key: "dialogPage.shortcutSave",
        keys: "mod+S"
    },
    {
        key: "dialogPage.shortcutPalette",
        keys: "mod+K"
    },
    {
        key: "dialogPage.shortcutUndo",
        keys: "mod+Z"
    }
];

const shortcutStyles = StyleSheet.create({
    shortcutRow: {
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center"
    }
});

const handleDialogAction = ({
    closeAnimation
}: DialogActionProps) => closeAnimation();

const DialogShowcase = ({
    contentJustify,
    isShowClose,
    variant,
    title,
    size,
    type
}: DialogShowcaseProps) => {
    const {
        t
    } = useDocsI18n();

    const {
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        isVisible,
        setIsVisible
    ] = useState(false);

    const renderItems = () => <Card
        spreadBehaviour="stretch"
        variant="filled"
    >
        <Card.Content>
            {
                SHORTCUT_ROWS.map(({
                    keys,
                    key
                }, index) => <View
                    style={[
                        shortcutStyles.shortcutRow,
                        {
                            borderTopColor: colors.content.border.subtle,
                            paddingVertical: spaces.spacingSm,
                            borderTopWidth: index > 0 ? 1 : 0
                        }
                    ]}
                    key={key}
                >
                    <Text
                        variant="bodyMediumSize"
                        color="high"
                    >
                        {t(key)}
                    </Text>
                    <Kbd
                        displayBehaviour="always"
                        keys={keys}
                    />
                </View>)
            }
        </Card.Content>
    </Card>;

    return <Fragment>
        <Button
            onPress={() => setIsVisible(true)}
            variant="outline"
            type="neutral"
            title={title}
        />
        <Dialog
            content={variant === "info" ? t("dialogPage.shortcutsDescription") : t("sample.description")}
            title={variant === "info" ? t("dialogPage.shortcutsTitle") : t("sample.title")}
            onClosed={() => setIsVisible(false)}
            onOverlayPress={handleDialogAction}
            secondaryButtonProps={{
                onPress: handleDialogAction
            }}
            primaryButtonProps={{
                onPress: handleDialogAction
            }}
            contentJustify={contentJustify}
            isShowClose={isShowClose}
            isOpen={isVisible}
            variant={variant}
            icon={InfoIcon}
            size={size}
            type={type}
        >
            {variant === "info" ? renderItems() : null}
        </Dialog>
    </Fragment>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("dialogPage.typesTitle"),
        id: "types"
    },
    {
        title: t("dialogPage.alignTitle"),
        id: "align"
    },
    {
        title: t("dialogPage.sizesTitle"),
        id: "sizes"
    },
    {
        title: t("dialogPage.bandTitle"),
        id: "band"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("dialogPage.imperativeTitle"),
        id: "imperative"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const DialogPage = () => {
    const {
        t
    } = useDocsI18n();

    const dialogRef = useRef<IDialogRef>(null);

    const [
        isDeleteOpen,
        setIsDeleteOpen
    ] = useState(false);

    const [
        isProjectOpen,
        setIsProjectOpen
    ] = useState(false);

    const [
        isInviteOpen,
        setIsInviteOpen
    ] = useState(false);

    const [
        email,
        setEmail
    ] = useState("grace@ncore.app");

    const [
        isSavingOpen,
        setIsSavingOpen
    ] = useState(false);

    const [
        isSaving,
        setIsSaving
    ] = useState(false);

    useEffect(() => {
        if(!isSaving) {
            return;
        }

        const timer = window.setTimeout(() => dialogRef.current?.closeAnimation(), SAVE_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isSaving
    ]);

    const handleSavingClosed = () => {
        setIsSavingOpen(false);

        setIsSaving(false);
    };

    const handleImperativeOpen = () => {
        NCoreUIKitDialog.open({
            content: t("dialogPage.imperativeContent"),
            title: t("dialogPage.imperativeHeading"),
            onOverlayPress: handleDialogAction,
            primaryButtonProps: {
                onPress: handleDialogAction,
                title: t("dialogPage.gotIt")
            },
            icon: InfoIcon,
            type: "info"
        });
    };

    return <Article>
        <ComponentHeader
            lead={t("dialogPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("dialogPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("dialogPage.overviewCaption")}
            >
                <Button
                    onPress={() => setIsDeleteOpen(true)}
                    title={t("dialogPage.deleteDraft")}
                    variant="outline"
                    type="danger"
                />
                <Dialog
                    content={t("dialogPage.deleteContent")}
                    onClosed={() => setIsDeleteOpen(false)}
                    title={t("dialogPage.deleteTitle")}
                    onOverlayPress={handleDialogAction}
                    primaryButtonProps={{
                        title: t("dialogPage.delete"),
                        onPress: handleDialogAction
                    }}
                    secondaryButtonProps={{
                        onPress: handleDialogAction,
                        title: t("sample.cancel")
                    }}
                    isOpen={isDeleteOpen}
                    variant="yes-no"
                    icon={InfoIcon}
                    type="danger"
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("dialogPage.variantsText")}
            </Paragraph>
            <Preview>
                {
                    VARIANTS.map((variant) => <DialogShowcase
                        variant={variant}
                        title={variant}
                        key={variant}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("dialogPage.typesTitle")}
            id="types"
        >
            <Paragraph>
                {t("dialogPage.typesText")}
            </Paragraph>
            <Preview>
                {
                    TYPES.map((type) => <DialogShowcase
                        variant="yes-no"
                        title={type}
                        type={type}
                        key={type}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("dialogPage.alignTitle")}
            id="align"
        >
            <Paragraph>
                {t("dialogPage.alignText")}
            </Paragraph>
            <Preview>
                {
                    JUSTIFIES.map((contentJustify) => <DialogShowcase
                        contentJustify={contentJustify}
                        title={contentJustify}
                        key={contentJustify}
                        variant="yes-no"
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("dialogPage.sizesTitle")}
            id="sizes"
        >
            <Paragraph>
                {t("dialogPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <DialogShowcase
                        contentJustify="language-based"
                        variant="yes-no"
                        title={size}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("dialogPage.bandTitle")}
            id="band"
        >
            <Paragraph>
                {t("dialogPage.bandText")}
            </Paragraph>
            <Preview
                caption={t("dialogPage.bandCaption")}
            >
                <Button
                    onPress={() => setIsProjectOpen(true)}
                    title={t("dialogPage.projectOpen")}
                    variant="outline"
                    type="danger"
                />
                <Dialog
                    onClosed={() => setIsProjectOpen(false)}
                    content={t("dialogPage.projectContent")}
                    title={t("dialogPage.projectTitle")}
                    onOverlayPress={handleDialogAction}
                    primaryButtonProps={{
                        title: t("dialogPage.delete"),
                        onPress: handleDialogAction
                    }}
                    secondaryButtonProps={{
                        onPress: handleDialogAction,
                        title: t("sample.cancel")
                    }}
                    contentJustify="language-based"
                    isOpen={isProjectOpen}
                    icon={Trash2Icon}
                    variant="yes-no"
                    type="danger"
                />
                <Button
                    onPress={() => setIsInviteOpen(true)}
                    title={t("dialogPage.inviteOpen")}
                    variant="outline"
                    type="neutral"
                />
                <Dialog
                    content={t("dialogPage.inviteDescription")}
                    onClosed={() => setIsInviteOpen(false)}
                    primaryButtonProps={{
                        title: t("dialogPage.inviteSend"),
                        onPress: handleDialogAction
                    }}
                    title={t("dialogPage.inviteTitle")}
                    onOverlayPress={handleDialogAction}
                    secondaryButtonProps={{
                        onPress: handleDialogAction,
                        title: t("sample.cancel")
                    }}
                    contentJustify="language-based"
                    isOpen={isInviteOpen}
                    variant="yes-no"
                >
                    <TextInput
                        title={t("dialogPage.inviteEmail")}
                        spreadBehaviour="stretch"
                        onChangeText={setEmail}
                        autoComplete="email"
                        value={email}
                    />
                </Dialog>
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("dialogPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("dialogPage.statesCaption")}
            >
                <Button
                    onPress={() => setIsSavingOpen(true)}
                    title={t("dialogPage.saveChanges")}
                    variant="outline"
                    type="neutral"
                />
                <Dialog
                    content={t("dialogPage.savingContent")}
                    primaryButtonProps={{
                        onPress: () => setIsSaving(true),
                        title: t("sample.action"),
                        isLoading: isSaving
                    }}
                    title={t("dialogPage.savingTitle")}
                    onOverlayPress={handleDialogAction}
                    secondaryButtonProps={{
                        onPress: handleDialogAction,
                        title: t("sample.cancel")
                    }}
                    onClosed={handleSavingClosed}
                    isOpen={isSavingOpen}
                    variant="yes-no"
                    ref={dialogRef}
                />
                <DialogShowcase
                    title={t("dialogPage.noClose")}
                    isShowClose={false}
                    variant="yes-no"
                />
            </Preview>
        </Section>
        <Section
            title={t("dialogPage.imperativeTitle")}
            id="imperative"
        >
            <Paragraph>
                {t("dialogPage.imperativeText")}
            </Paragraph>
            <Preview>
                <Button
                    title={t("dialogPage.imperativeOpen")}
                    onPress={handleImperativeOpen}
                    variant="outline"
                    type="neutral"
                />
            </Preview>
            <CodeBlock
                code={IMPERATIVE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("dialogPage.a11yRole"),
                    t("dialogPage.a11yLabel"),
                    t("dialogPage.a11yFocus"),
                    t("dialogPage.a11yDismiss"),
                    t("dialogPage.a11yLoading"),
                    t("dialogPage.a11yClose"),
                    t("dialogPage.a11yBare"),
                    t("dialogPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("dialogPage.propIsOpen"),
                        defaultValue: "false",
                        name: "isOpen",
                        type: "boolean"
                    },
                    {
                        description: t("dialogPage.propOnOpenChange"),
                        type: "(isOpen: boolean) => void",
                        name: "onOpenChange"
                    },
                    {
                        description: t("dialogPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("dialogPage.propContent"),
                        name: "content",
                        type: "string"
                    },
                    {
                        description: t("dialogPage.propVariant"),
                        type: "\"yes-no\" | \"ok\" | \"info\"",
                        defaultValue: "\"ok\"",
                        name: "variant"
                    },
                    {
                        type: "\"primary\" | \"neutral\" | \"success\" | \"warning\" | \"danger\" | \"info\"",
                        description: t("dialogPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("dialogPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("dialogPage.propContentJustify"),
                        type: "\"centered\" | \"language-based\"",
                        defaultValue: "\"centered\"",
                        name: "contentJustify"
                    },
                    {
                        type: "\"xSmall\" | \"small\" | \"medium\" | \"large\"",
                        description: t("dialogPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("dialogPage.propIsShowClose"),
                        defaultValue: "true",
                        name: "isShowClose",
                        type: "boolean"
                    },
                    {
                        type: "Omit<IButtonProps, \"onPress\"> & { onPress?({ closeAnimation }) }",
                        description: t("dialogPage.propPrimaryButtonProps"),
                        name: "primaryButtonProps"
                    },
                    {
                        type: "Omit<IButtonProps, \"onPress\"> & { onPress?({ closeAnimation }) }",
                        description: t("dialogPage.propSecondaryButtonProps"),
                        name: "secondaryButtonProps"
                    },
                    {
                        description: t("dialogPage.propOnOverlayPress"),
                        type: "({ closeAnimation }) => void",
                        name: "onOverlayPress"
                    },
                    {
                        description: t("dialogPage.propOnClosed"),
                        type: "({ id }) => void",
                        name: "onClosed"
                    },
                    {
                        description: t("dialogPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        description: t("dialogPage.propHeaderComponent"),
                        name: "headerComponent",
                        type: "ReactNode"
                    },
                    {
                        description: t("dialogPage.propBottomComponent"),
                        name: "bottomComponent",
                        type: "ReactNode"
                    },
                    {
                        description: t("dialogPage.propWithModal"),
                        defaultValue: "true",
                        name: "withModal",
                        type: "boolean"
                    },
                    {
                        description: t("dialogPage.propModalProps"),
                        type: "Omit<IModalProps, \"id\">",
                        name: "modalProps"
                    },
                    {
                        description: t("dialogPage.propCloseIconProps"),
                        type: "{ size, color, customColor }",
                        name: "closeIconProps"
                    },
                    {
                        description: t("dialogPage.propOpenAnimationDelay"),
                        name: "openAnimationDelay",
                        type: "number"
                    },
                    {
                        description: t("dialogPage.propCloseAnimationDelay"),
                        name: "closeAnimationDelay",
                        type: "number"
                    },
                    {
                        description: t("dialogPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("dialogPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("dialogPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("dialogPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default DialogPage;
